// apps/widget/src/components/report-visual.tsx
//
// The right half of the report screen: this visitor's own scores. One ranked
// strip of every metric, dragged or swiped sideways; tapping one selects it,
// shows its detail above the strip and brings its card into view on the left.
//
// The scan photo is not shown — it is deleted straight after analysis, which is
// a promise made on the consent screen. Showing it back would mean keeping it.

import { useEffect, useId, useRef, useState } from 'react';
import type { Report } from '../lib/api';
import { t, metricLabel } from '../lib/i18n';
import { BAND_COLORS } from '../lib/theme';
import { useScan, type CapturedFace } from '../state';
import { faceFrame, regionShapes } from '../lib/face-regions';
import { REGION_LANDMARKS } from '../lib/facemesh';
import { marksFor, type Marks } from '../lib/skin-marks';
import type { FaceRegion } from '@skinvizo/shared';

/** Pixels a pointer may move and still count as a tap rather than a drag. */
const TAP_SLOP = 6;
/** Per-frame decay of the release velocity; lower stops sooner. */
const FRICTION = 0.94;

export function ReportVisual({
  report,
  active,
  onSelect,
}: {
  report: Report;
  /** Owned by the report screen, so a tap on a card and a tap on a ring
   *  select the same thing. */
  active: string | null;
  onSelect: (key: string) => void;
}) {
  const { state } = useScan();
  const capture = state.capture;
  // Weakest first: the strip opens on what the plan is about to address.
  const rings = [...report.metrics].sort((a, b) => a.score - b.score);
  const selected = rings.find((m) => m.key === active) ?? null;

  const strip = useRef<HTMLDivElement>(null);
  const drag = useRef({ down: false, startX: 0, startLeft: 0, moved: 0, lastX: 0, lastT: 0, v: 0 });
  const glide = useRef(0);

  useEffect(() => () => cancelAnimationFrame(glide.current), []);

  // A selection made from a card brings its ring into view. Scrolled on the
  // strip directly: scrollIntoView would also move the report column.
  useEffect(() => {
    const el = strip.current;
    const ring = el?.querySelector<HTMLElement>('[data-active]');
    if (!el || !ring) return;
    const target = ring.offsetLeft - (el.clientWidth - ring.offsetWidth) / 2;
    el.scrollTo({ left: target, behavior: 'smooth' });
  }, [active]);

  /** Snap is off from first movement until the glide has stopped. Left on, it
   *  pulled every frame of the drag and of the glide back to the nearest ring. */
  const setFree = (free: boolean) => {
    if (!strip.current) return;
    if (free) strip.current.dataset.dragging = '';
    else delete strip.current.dataset.dragging;
  };

  // Mouse only: touch and trackpads already scroll this natively and smoothly,
  // and intercepting them would make them worse, not better.
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || !strip.current) return;
    cancelAnimationFrame(glide.current);
    drag.current = {
      down: true,
      startX: e.clientX,
      startLeft: strip.current.scrollLeft,
      moved: 0,
      lastX: e.clientX,
      lastT: performance.now(),
      v: 0,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d.down || !strip.current) return;
    d.moved = Math.max(d.moved, Math.abs(e.clientX - d.startX));
    if (d.moved > TAP_SLOP && strip.current.dataset.dragging === undefined) {
      setFree(true);
      strip.current.setPointerCapture(e.pointerId);
    }
    const now = performance.now();
    // Velocity from the most recent movement, for the glide on release.
    d.v = (e.clientX - d.lastX) / Math.max(1, now - d.lastT);
    d.lastX = e.clientX;
    d.lastT = now;
    strip.current.scrollLeft = d.startLeft - (e.clientX - d.startX);
  };

  const onPointerUp = () => {
    const d = drag.current;
    d.down = false;
    if (!strip.current || strip.current.dataset.dragging === undefined) return;

    // A pause before letting go means the throw has already stopped.
    const sinceLastMove = performance.now() - d.lastT;
    let velocity = sinceLastMove > 80 ? 0 : -d.v * 16;

    // Carry the throw on, decaying — without this a fast flick stopped dead
    // under the cursor, which is most of what made it feel rough.
    const step = () => {
      const el = strip.current;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      if (Math.abs(velocity) < 0.4 || el.scrollLeft <= 0 || el.scrollLeft >= max) {
        // Only now hand back to snap, so it settles on a ring at rest.
        setFree(false);
        return;
      }
      el.scrollLeft += velocity;
      velocity *= FRICTION;
      glide.current = requestAnimationFrame(step);
    };
    glide.current = requestAnimationFrame(step);
  };

  const select = (key: string) => {
    // A drag that ends over a ring is not a tap on it.
    if (drag.current.moved > TAP_SLOP) return;
    onSelect(key);
    // Bring the matching card into view on the left, so the two halves agree.
    document
      .getElementById(`gs-metric-${key}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="gs-welcome__visual">
      {/* The visitor's own photo, with the selected score's area marked, when
          it was captured in this tab. A report opened later from a link has no
          photo — it was deleted after analysis — so it falls back to the
          brand backdrop. */}
      {capture ? (
        <AnnotatedFace
          capture={capture}
          metric={selected?.key}
          score={selected?.score}
          regions={(selected?.regions ?? []) as FaceRegion[]}
          colour={
            selected ? BAND_COLORS[selected.band as keyof typeof BAND_COLORS] : '#fff'
          }
          label={selected ? metricLabel(selected.key, selected.label) : ''}
        />
      ) : (
        <div className="gs-welcome__preview" aria-hidden="true" />
      )}

      <div
        className={
          capture
            ? 'gs-welcome__scores-block gs-welcome__scores-block--photo'
            : 'gs-welcome__scores-block'
        }
      >
        <div className="gs-welcome__scores">
          <span>
            <span className="gs-welcome__score-label">{t('report.skinScore')}</span>
            <br />
            <span className="gs-welcome__score-value">{report.skinScore}</span>
          </span>
          <span>
            <span className="gs-welcome__score-label">{t('report.skinAge')}</span>
            <br />
            <span className="gs-welcome__score-value">{report.skinAge}</span>
          </span>
        </div>

        <div
          ref={strip}
          className="gs-welcome__rings gs-rings-strip"
          role="listbox"
          aria-label={t('report.metrics')}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {rings.map((m) => (
            <button
              key={m.key}
              type="button"
              role="option"
              aria-selected={m.key === active}
              className="gs-welcome__ring gs-ring-btn"
              data-active={m.key === active || undefined}
              onClick={() => select(m.key)}
            >
              <b style={{ borderColor: BAND_COLORS[m.band as keyof typeof BAND_COLORS] }}>{m.score}</b>
              {metricLabel(m.key, m.label)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The captured photo, framed on the face, with the selected metric's area drawn
 * over it. Image and shapes share one SVG coordinate space, so they stay aligned
 * at any panel size — the shapes are never positioned against CSS boxes.
 */
export function AnnotatedFace({
  capture,
  metric,
  score,
  regions,
  colour,
  label,
  focus = false,
  className = 'gs-face',
}: {
  capture: CapturedFace;
  /** The metric whose marks to draw; without one, its regions are shaded. */
  metric?: string | undefined;
  score?: number | undefined;
  regions: FaceRegion[];
  colour: string;
  label: string;
  /** Crop in on the marked area rather than framing the whole face — for the
   *  small photo on each concern card, where a whole face leaves the mark tiny. */
  focus?: boolean;
  className?: string;
}) {
  const { width, height, landmarks, photoUrl } = capture;
  const whole = faceFrame(landmarks, width, height);
  const frame = focus ? focusFrame(landmarks, regions, width, height, whole) : whole;
  const stroke = Math.max(2, frame.w * 0.006);
  const clip = useId();

  // Measured from this photo in this tab; see lib/skin-marks. Until they are
  // ready, and when the photo cannot be read, the area is shaded instead.
  const [marks, setMarks] = useState<{ key: string; marks: Marks | null } | null>(null);
  const key = `${photoUrl}|${metric ?? ''}|${score ?? ''}`;
  useEffect(() => {
    if (!metric || score === undefined) return;
    let live = true;
    void marksFor(capture, metric, regions, score).then((found) => {
      if (live) setMarks({ key, marks: found });
    });
    return () => {
      live = false;
    };
    // `regions` follows `metric`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const current = marks?.key === key ? marks.marks : undefined;
  const pending = Boolean(metric) && current === undefined;
  const shade = !pending && (!current || current.empty);
  const shapes = shade ? regions.flatMap((r) => regionShapes(landmarks, r, width, height)) : [];

  return (
    <svg
      className={className}
      viewBox={`${frame.x} ${frame.y} ${frame.w} ${frame.h}`}
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label={label ? t('a11y.photoArea', { area: label }) : t('a11y.photo')}
    >
      <image href={photoUrl} x={0} y={0} width={width} height={height} />

      {current && !current.empty && (
        // Keyed so a new selection replays the reveal.
        <g key={key} className="gs-face__marks">
          {current.layer && (
            <image
              href={current.layer.url}
              x={current.layer.box.x}
              y={current.layer.box.y}
              width={current.layer.box.w}
              height={current.layer.box.h}
              preserveAspectRatio="none"
            />
          )}
          {current.dots.map((d, i) => (
            <circle
              key={i}
              cx={d.x}
              cy={d.y}
              r={d.r}
              fill={current.colour}
              fillOpacity={0.35}
              stroke={current.colour}
              strokeWidth={Math.max(1, d.r * 0.35)}
            />
          ))}
          {current.contours.length > 0 && (
            <>
              <clipPath id={clip}>
                {regionShapes(landmarks, 'fullFace', width, height).map((s, i) => (
                  <path key={i} d={s.d} />
                ))}
              </clipPath>
              <g clipPath={`url(#${CSS.escape(clip)})`}>
                {current.contours.map((d, i) => (
                  <path key={i} d={d} fill="none" stroke={current.colour} strokeOpacity={0.85} strokeWidth={stroke * 0.9} />
                ))}
              </g>
            </>
          )}
          {current.outline && (
            <path
              d={current.outline}
              fill={current.colour}
              fillOpacity={0.08}
              stroke={current.colour}
              strokeOpacity={0.9}
              strokeWidth={stroke * 1.4}
            />
          )}
        </g>
      )}

      {shade && (
        <g key={label} className="gs-face__marks">
          {shapes.map((s, i) =>
            s.closed ? (
              <path
                key={i}
                d={s.d}
                fill={colour}
                fillOpacity={0.26}
                stroke={colour}
                strokeWidth={stroke}
                strokeLinejoin="round"
              />
            ) : (
              <path
                key={i}
                d={s.d}
                fill="none"
                stroke={colour}
                strokeWidth={stroke * 1.6}
                strokeLinecap="round"
              />
            ),
          )}
        </g>
      )}
    </svg>
  );
}

/**
 * A frame around just the given regions, never narrower than half the face so
 * there is enough of it around the mark to tell where on the face it is.
 */
function focusFrame(
  landmarks: { x: number; y: number }[],
  regions: FaceRegion[],
  width: number,
  height: number,
  whole: { x: number; y: number; w: number; h: number },
) {
  const pts = regions
    .flatMap((r) => REGION_LANDMARKS[r] ?? [])
    .map((i) => landmarks[i])
    .filter((p): p is { x: number; y: number } => p !== undefined);
  if (pts.length < 3) return whole;

  const xs = pts.map((p) => p.x * width);
  const ys = pts.map((p) => p.y * height);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  // Square-ish crop, padded around the area, clamped between half and all of
  // the face frame.
  const span = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)) * 1.5;
  const w = Math.min(whole.w, Math.max(whole.w * 0.5, span));
  const h = Math.min(whole.h, w * 1.1);
  const x = Math.max(0, Math.min(width - w, cx - w / 2));
  const y = Math.max(0, Math.min(height - h, cy - h / 2));
  return { x, y, w, h };
}
